import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Save, X, ArrowLeftRight } from 'lucide-react';
import { FormSection, Input, Select, Button, Textarea } from '../../common/ui';
import DeviceAutocomplete from '../../common/DeviceAutocomplete';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { DEVICE_STATUS_OPTIONS } from '../../../utils/statusUtils';
import { normalizeSerialNumber, validateSerialNumber } from '../../../utils/normalizeSerialNumber';

const DeviceInfoSection = forwardRef(({ deviceData, readOnly = true, onSave, onChangeDevice, warrantySince = null, externalEditMode }, ref) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isProductEditable, setIsProductEditable] = useState(false);
  const [editedData, setEditedData] = useState({
    serial_number: deviceData.serial_number || '',
    product_name: deviceData.product_name || deviceData.model || '',
    product_id: deviceData.product_id || null,
    status: deviceData.status || 'active',
    purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
    notes: deviceData.notes || ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Device swap state
  const [isChangingDevice, setIsChangingDevice] = useState(false);
  const [swapSearchValue, setSwapSearchValue] = useState('');
  const [selectedSwapDevice, setSelectedSwapDevice] = useState(null);
  const [isSwapping, setIsSwapping] = useState(false);

  const effectiveEditing = externalEditMode === 'edit' || isEditing;

  // Sync local editing state with external edit mode
  useEffect(() => {
    if (externalEditMode === 'edit' && !isEditing) {
      setEditedData({
        serial_number: deviceData.serial_number || '',
        product_name: deviceData.product_name || deviceData.model || '',
        product_id: deviceData.product_id || null,
        status: deviceData.status || 'active',
        purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
        notes: deviceData.notes || ''
      });
      setIsEditing(true);
    } else if (externalEditMode !== 'edit' && isEditing && !isChangingDevice) {
      setIsEditing(false);
    }
  }, [externalEditMode]);

  useImperativeHandle(ref, () => ({
    getEditedData: () => ({
      ...editedData,
      serial_number: normalizeSerialNumber(editedData.serial_number) || editedData.serial_number
    }),
    hasChanges: () => {
      const origSerial = normalizeSerialNumber(deviceData.serial_number || '') || (deviceData.serial_number || '').trim();
      const currSerial = normalizeSerialNumber(editedData.serial_number || '') || (editedData.serial_number || '').trim();
      const orig = {
        serial_number: origSerial,
        product_id: deviceData.product_id || null,
        status: (deviceData.status || 'active'),
        purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
        notes: (deviceData.notes || '').trim()
      };
      const curr = {
        serial_number: currSerial,
        product_id: editedData.product_id || null,
        status: (editedData.status || 'active'),
        purchase_date: editedData.purchase_date || '',
        notes: (editedData.notes || '').trim()
      };
      return JSON.stringify(orig) !== JSON.stringify(curr);
    },
    validate: () => {
      const normalizedSerial = normalizeSerialNumber(editedData.serial_number);
      if (!normalizedSerial) {
        return { valid: false, error: 'Serial number is required' };
      }
      const serialCheck = validateSerialNumber(normalizedSerial);
      if (!serialCheck.valid) {
        return { valid: false, error: serialCheck.error };
      }
      if (!editedData.product_id) {
        return { valid: false, error: 'Please select a valid product from the list' };
      }
      if (editedData.purchase_date) {
        const selectedDate = new Date(editedData.purchase_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate > today) {
          return { valid: false, error: 'Warranty begin date cannot be in the future' };
        }
        if (isNaN(selectedDate.getTime())) {
          return { valid: false, error: 'Please enter a valid date' };
        }
      }
      return { valid: true, error: null };
    },
    reset: () => {
      setEditedData({
        serial_number: deviceData.serial_number || '',
        product_name: deviceData.product_name || deviceData.model || '',
        product_id: deviceData.product_id || null,
        status: deviceData.status || 'active',
        purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
        notes: deviceData.notes || ''
      });
      setValidationError('');
    }
  }));

  // Update internal state when deviceData prop changes
  useEffect(() => {
    if (!effectiveEditing) {
      setEditedData({
        serial_number: deviceData.serial_number || '',
        product_name: deviceData.product_name || deviceData.model || '',
        product_id: deviceData.product_id || null,
        status: deviceData.status || 'active',
        purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
        notes: deviceData.notes || ''
      });
    }
  }, [deviceData.serial_number, deviceData.product_name, deviceData.model, deviceData.product_id, deviceData.status, deviceData.purchase_date, deviceData.notes, effectiveEditing]);

  const handleEdit = () => {
    setEditedData({
      serial_number: deviceData.serial_number || '',
      product_name: deviceData.product_name || deviceData.model || '',
      product_id: deviceData.product_id || null,
      status: deviceData.status || 'active',
      purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
      notes: deviceData.notes || ''
    });
    setValidationError('');
    setIsEditing(true);
    setIsProductEditable(false);
  };

  const handleCancel = () => {
    setEditedData({
      serial_number: deviceData.serial_number || '',
      product_name: deviceData.product_name || deviceData.model || '',
      product_id: deviceData.product_id || null,
      status: deviceData.status || 'active',
      purchase_date: deviceData.purchase_date ? deviceData.purchase_date.split('T')[0] : '',
      notes: deviceData.notes || ''
    });
    setValidationError('');
    setIsEditing(false);
    setIsProductEditable(false);
  };

  const handleSave = async () => {
    // Normalize serial number before validation
    const normalizedSerial = normalizeSerialNumber(editedData.serial_number);

    if (!normalizedSerial) {
      setValidationError('Serial number is required');
      return;
    }

    // Validate serial number characters
    const serialCheck = validateSerialNumber(normalizedSerial);
    if (!serialCheck.valid) {
      setValidationError(serialCheck.error);
      return;
    }

    if (!editedData.product_id) {
      setValidationError('Please select a valid product from the list');
      return;
    }

    // Validate warranty begin date if provided
    if (editedData.purchase_date) {
      const selectedDate = new Date(editedData.purchase_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate > today) {
        setValidationError('Warranty begin date cannot be in the future');
        return;
      }
      
      // Check if date is valid
      if (isNaN(selectedDate.getTime())) {
        setValidationError('Please enter a valid date');
        return;
      }
    }

    if (onSave) {
      setIsSaving(true);
      setValidationError('');
      try {
        // Update the serial number with the normalized value before saving
        const dataToSave = { ...editedData, serial_number: normalizedSerial };
        await onSave(dataToSave);
        setIsEditing(false);
        setIsProductEditable(false);
      } catch (error) {
        console.error('Error saving device information:', error);
        setValidationError('Failed to save changes');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  // Device swap handlers
  const handleStartSwap = () => {
    setSwapSearchValue('');
    setSelectedSwapDevice(null);
    setValidationError('');
    setIsChangingDevice(true);
  };

  const handleCancelSwap = () => {
    setSwapSearchValue('');
    setSelectedSwapDevice(null);
    setValidationError('');
    setIsChangingDevice(false);
  };

  const handleSwapDeviceSelect = (device) => {
    if (String(device.id) === String(deviceData.id)) {
      setValidationError('This is already the current device');
      setSelectedSwapDevice(null);
      return;
    }
    setValidationError('');
    setSelectedSwapDevice(device);
  };

  const handleConfirmSwap = async () => {
    if (!selectedSwapDevice) {
      setValidationError('Please select a device');
      return;
    }
    if (onChangeDevice) {
      setIsSwapping(true);
      setValidationError('');
      try {
        await onChangeDevice(selectedSwapDevice.id);
        setIsChangingDevice(false);
        setSelectedSwapDevice(null);
        setSwapSearchValue('');
      } catch (error) {
        console.error('Error changing device:', error);
        setValidationError(error.response?.data?.error || 'Failed to change device');
      } finally {
        setIsSwapping(false);
      }
    }
  };

  // Extract WooCommerce order ID from URL (same logic as OwnerInfoSection)
  const extractWooCommerceId = (url) => {
    if (!url) return null;
    // If it's already just a number, return it
    if (/^\d+$/.test(String(url))) {
      return String(url);
    }
    try {
      // Parse URL to extract query parameters
      const urlObj = new URL(url);
      const postId = urlObj.searchParams.get('post');
      // Check if it's a number
      if (postId && /^\d+$/.test(postId)) {
        return postId;
      }
      // HPOS format: admin.php?page=wc-orders&action=edit&id=123
      if (urlObj.searchParams.get('page') === 'wc-orders') {
        const hposId = urlObj.searchParams.get('id');
        if (hposId && /^\d+$/.test(hposId)) {
          return hposId;
        }
      }
    } catch (e) {
      // Invalid URL, return null
      return null;
    }
    return null;
  };

  const renderActions = () => {
    if (readOnly) return null;

    // When external edit mode is active, hide per-card buttons but keep change device in non-edit states
    if (externalEditMode === 'edit') return null;

    if (isEditing) {
      return (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            variant="primary"
            size="xs"
            icon={Save}
            loading={isSaving}
          >
            Save
          </Button>
          <Button
            onClick={handleCancel}
            variant="ghost"
            size="xs"
            icon={X}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      );
    }

    if (isChangingDevice) {
      return (
        <Button
          onClick={handleCancelSwap}
          variant="ghost"
          size="xs"
          icon={X}
          disabled={isSwapping}
        >
          Cancel
        </Button>
      );
    }

    return (
      <div className="flex gap-1">
        {onChangeDevice && !externalEditMode && (
          <button
            onClick={handleStartSwap}
            className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
            title="Change device"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>
        )}
        {!externalEditMode && (
          <button
            onClick={handleEdit}
            className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
            title="Edit device information"
          >
            <Edit2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <FormSection title="Device Information" actions={renderActions()}>
      {validationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{validationError}</p>
        </div>
      )}
      
      <div className="space-y-1.5">
        {isChangingDevice ? (
          <>
            {/* Change Device Mode */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
              <div>
                <span className="text-sm font-medium text-gray-700">Current device: </span>
                <span className="text-sm text-gray-900">
                  {deviceData.product_name || deviceData.model || 'Unknown'} — {deviceData.serial_number || 'No serial'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search for new device
                </label>
                <DeviceAutocomplete
                  value={swapSearchValue}
                  onChange={(value) => {
                    setSwapSearchValue(value);
                    if (selectedSwapDevice) {
                      setSelectedSwapDevice(null);
                    }
                  }}
                  onDeviceSelect={handleSwapDeviceSelect}
                  placeholder="Enter serial number..."
                  required
                />
              </div>

              {selectedSwapDevice && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <span className="text-sm font-medium text-blue-800">New device: </span>
                  <span className="text-sm text-blue-900">
                    {selectedSwapDevice.product_name || selectedSwapDevice.model || 'Unknown'} — {selectedSwapDevice.serial_number}
                  </span>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={handleConfirmSwap}
                  variant="primary"
                  size="sm"
                  loading={isSwapping}
                  disabled={!selectedSwapDevice}
                >
                  Confirm Change
                </Button>
              </div>
            </div>
          </>
        ) : effectiveEditing ? (
          <>
            {/* Edit Mode - Compact */}
            <div className="bg-gray-50/70 rounded p-2 space-y-1.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Product
                  </label>
                  {!isProductEditable ? (
                    <div>
                      <Input
                        size="sm"
                        value={editedData.product_name || editedData.model || ''}
                        disabled
                        className="bg-gray-50 cursor-not-allowed"
                      />
                      <button 
                        type="button"
                        onClick={() => setIsProductEditable(true)}
                        className="mt-0.5 text-xs text-blue-600 hover:text-blue-800 hover:underline focus:outline-none"
                      >
                        Change product
                      </button>
                    </div>
                  ) : (
                    <DeviceAutocomplete
                      value={editedData.product_name || ''}
                      onChange={(value) => {
                        handleChange('product_name', value);
                        handleChange('product_id', null);
                      }}
                      onDeviceSelect={(item) => {
                        handleChange('product_name', item.name);
                        handleChange('product_id', item.id);
                      }}
                      searchType="products"
                      placeholder="Search for product..."
                      required
                    />
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Serial number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="sm"
                    value={editedData.serial_number}
                    onChange={(e) => handleChange('serial_number', e.target.value)}
                    onBlur={() => handleChange('serial_number', normalizeSerialNumber(editedData.serial_number) || '')}
                    placeholder="Enter device serial number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Status
                  </label>
                  <Select
                    size="sm"
                    value={editedData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    options={DEVICE_STATUS_OPTIONS}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Warranty begin date
                  </label>
                  <Input
                    size="sm"
                    type="date"
                    value={editedData.purchase_date}
                    onChange={(e) => handleChange('purchase_date', e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Internal notes
                  </label>
                  <Textarea
                    size="sm"
                    value={editedData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Internal notes..."
                    rows={2}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Read-Only Mode - Zebra rows */}
            <div className="rounded overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 py-1 px-2 bg-gray-50/70">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Product</span>
                  <span className="text-sm text-gray-900">{deviceData.product_name || deviceData.model || 'N/A'}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Serial</span>
                  {deviceData.serial_number ? (
                    <a
                      href={`/devices/id/${deviceData.id}/view`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/devices/id/${deviceData.id}/view`);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      {deviceData.serial_number}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 py-1 px-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Manufactured</span>
                  <span className="text-sm text-gray-900">{deviceData.created_at ? formatDateTime(deviceData.created_at) : 'N/A'}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Warranty begin</span>
                  <span className="text-sm text-gray-900">
                    {deviceData.purchase_date
                      ? formatDate(deviceData.purchase_date)
                      : warrantySince
                        ? formatDate(warrantySince)
                        : 'Not set'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 py-1 px-2 bg-gray-50/70">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Status</span>
                  <span className="text-sm text-gray-900 capitalize">{deviceData.status || 'N/A'}</span>
                </div>
                {deviceData.order_id && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Sold to</span>
                    <a
                      href={`/orders/${deviceData.order_id}/serials`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/orders/${deviceData.order_id}/serials`);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      {deviceData.order_company_name && deviceData.order_company_name.trim()
                        ? deviceData.order_company_name.trim()
                        : deviceData.order_customer_name || 'N/A'}
                    </a>
                  </div>
                )}
              </div>
              {deviceData.order_id && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 py-1 px-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Order</span>
                    {deviceData.order_number ? (
                      <a
                        href={deviceData.order_number.includes('http') 
                          ? deviceData.order_number 
                          : `https://b2b.naviter.com/wp-admin/post.php?post=${deviceData.order_number}&action=edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                        title={deviceData.order_number.includes('http') ? deviceData.order_number : undefined}
                      >
                        #{extractWooCommerceId(deviceData.order_number) || deviceData.order_number}
                      </a>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            {deviceData.order_history && deviceData.order_history.filter(h => h.unassigned_at).length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Previous Orders</span>
                <div className="mt-1 space-y-1">
                  {deviceData.order_history
                    .filter(h => h.unassigned_at)
                    .map((h, idx) => (
                      <div key={h.assignment_id || idx} className="flex items-center text-sm bg-gray-50 rounded px-2 py-1">
                        <div className="flex-1 min-w-0">
                          <a
                            href={`/orders/${h.order_id}/serials`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/orders/${h.order_id}/serials`);
                            }}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            #{h.order_number}
                          </a>
                          <span className="text-gray-500 mx-1">-</span>
                          <span className="text-gray-700">
                            {h.company_name && h.company_name.trim()
                              ? h.company_name.trim()
                              : h.customer_name || 'Unknown'}
                          </span>
                          {h.unassignment_reason && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                              {h.unassignment_reason}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-400 text-xs ml-2 whitespace-nowrap">
                          {h.assigned_at ? formatDate(h.assigned_at) : '?'}
                          {' \u2192 '}
                          {h.unassigned_at ? formatDate(h.unassigned_at) : '?'}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {deviceData.notes && (
              <div className="mt-2 border-l-2 border-gray-200 pl-3">
                <span className="text-xs uppercase tracking-wide font-medium text-gray-500">Internal notes</span>
                <p className="text-sm text-gray-900 whitespace-pre-wrap mt-0.5">{deviceData.notes}</p>
              </div>
            )}
          </>
        )}
      </div>
    </FormSection>
  );
});

DeviceInfoSection.displayName = 'DeviceInfoSection';

export default DeviceInfoSection;

